import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup, FormsModule } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AcademicYearState } from './academic-year/academic-year.state';
import { ChildComponent } from './child/child.component';
import { ChildState } from './child/child.state';
import { GuardianComponent } from './guardian/guardian.component';
import { GuardianState } from './guardian/guardian.state';
import { SaveService } from './services/save.service';
import { RootActions } from './store/root.actions';
import { RootState } from './store/root.state';
import { TermsConditionsComponent } from './terms-conditions/terms-conditions.component';
import { TermsConditionsState } from './terms-conditions/terms-conditions.state';
import { Application, Household, IApplicationContent, ApplicationForSchool } from './types';
import { ISchool } from './types/school.type';
import { NearbySchoolsState } from './nearby-schools/nearby-schools.state';
import { AcademicYearComponent } from './academic-year/academic-year.component';
import { IAppSettings, APP_CONFIG } from '@nha/config/interfaces/iapp-settings.interface';
import { HowHeardComponent } from './how-heard/how-heard.component';
import { SubmittedComponent } from './submitted/submitted.component';
import { NearbySchoolsComponent } from './nearby-schools/nearby-schools.component';
import { NgIf, NgFor, AsyncPipe, CommonModule } from '@angular/common';

declare const NHAUtil: any;
declare const $: any;

const getParams = (query: string) => {
	if (!query) {
		return {};
	}

	return (/^[?]/.test(query) ? query.slice(1) : query).split('&').reduce((params, param) => {
		const [key, value] = param.split('=');
		params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
		return params;
	}, {});
};

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: true,
    imports: [NgIf, CommonModule, FormsModule, NgFor, AcademicYearComponent, GuardianComponent, ChildComponent, NearbySchoolsComponent, TermsConditionsComponent, SubmittedComponent, HowHeardComponent, AsyncPipe]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	content$: Observable<IApplicationContent>;
	appSettings = inject<IAppSettings>(APP_CONFIG);

	showHowHeard = false;
	schoolID: number = window['nha-sa-schoolId'] || -1;
	culture: string = window['nha-sa-culture'] || 'en-US';
	loadError = false;
	isSaving = false;
	isSubmitted = false;
	appData: Application;
	uniqueHouseholds: Household[];
	today: Date = new Date();
	enrollIndy = false;
	currentAcademicYearUnavailable = false;
	showSchoolPicker = this.schoolID === -1 || this.appSettings.showApplicationSchoolPicker;
	schoolList: ISchool[];
	kenticoUrl: string = this.appSettings.web.schoolSites;
	placementTestSchoolIDs: number[] = [437, 354, 335]; //Pembroke, Alliance, Knapp
	willowID = 404;
	showTermsAndConditions = false;

	private contentSub: Subscription = null;
	private schoolsSub: Subscription = null;

	@ViewChild(AcademicYearComponent) private academicYearComponent: AcademicYearComponent;
	@ViewChild(GuardianComponent) private guardianComponent: GuardianComponent;
	@ViewChild(ChildComponent) private childComponent: ChildComponent;
	@ViewChild(TermsConditionsComponent)
	private termsConditionsComponent: TermsConditionsComponent;

	constructor(private saveService: SaveService, private store: Store) {
		this.content$ = this.store.select(RootState.content);
	}

	ngOnInit(): void {
		

		const params = getParams(window.location.search);
		this.showHowHeard = params['howHeard'] || false;

		if (this.showSchoolPicker) {
			this.store.dispatch(new RootActions.GetSchools(this.culture)).subscribe((result: any) => {
				this.schoolList = result.rootState.schoolList;
			});
		}

		this.getContent();

		this.content$.subscribe((content: IApplicationContent) => {
			// set crm lead id
			if (content?.crmGuid) {
                   (window as any).gtag('set', 'user_properties', { 'crm_lead_id': content.crmGuid });
			}
			if (content?.alternativeLanguagePDFs) {
				setTimeout(() => {
					($('.nha-app-alt-lan-pdfs') as any).modaal();
				}, 500);
			}
		});
	}

	ngOnDestroy(): void {
		if (this.contentSub !== null) {
			this.contentSub.unsubscribe();
		}
		if (this.schoolsSub !== null) {
			this.schoolsSub.unsubscribe();
		}
	}

	ngAfterViewInit(): void {
		NHAUtil.configureTooltips();
	}

	openPembrokeInfoModal(): void {
		($('.nha-sa-pembroke-info') as any).modaal({
			start_open: true
		});
	}

	openWillowInfoModal(): void {
		($('.nha-sa-willow-info') as any).modaal({
			start_open: true
		});
	}

	IsPlacementTestSchool(schoolID: number): boolean {
		if (this.placementTestSchoolIDs.includes(schoolID)) {
			return true;
		}
		return false;
	}

	IsWillowForPlacement(schoolID: number): boolean {
		if (schoolID == this.willowID) {
			return true;
		}
		return false;
	}

	getContent(): void {
		if (this.schoolID && this.schoolID !== -1) {
			this.store.dispatch(new RootActions.GetContent(this.schoolID, this.culture)).pipe(
				catchError((error) => {
					this.loadError = true;
					return of(error);
				})
			);
		}
	}

	switchLanguage(): void {
		if (this.culture === 'en-US') {
			this.culture = 'es-MX';
		} else {
			this.culture = 'en-US';
		}

		// setting window for how heard still. Clean this up.
		window['nha-sa-culture'] = this.culture;

		this.getContent();
	}

	submitApplicationClick(): void {
		this.isSaving = true;

		this.guardianComponent.mapHouseholdsOnSave();

		this.academicYearComponent.academicYearForm.markAllAsTouched();
		this.guardianComponent.guardianForm.markAllAsTouched();
		this.childComponent.childForm.markAllAsTouched();
		this.termsConditionsComponent.termsConditionsForm.markAllAsTouched();

		setTimeout(() => {
			const errors = document.getElementsByClassName('ng-touched ng-invalid');
			if (errors.length > 0) {
				for (let i = 0; i < errors.length; i++) {
					const field = errors[i] as HTMLInputElement;
					if (
						field.type !== undefined ||
						field.className.indexOf('nha-sa-residency-container') > -1 ||
						field.className.indexOf('nha-invalid-birthdate') > -1
					) {
						let offset = (<any>field.offsetParent).offsetTop;
						if (field.id.indexOf('nearbyschool-gradeLevelId') === 0) {
							offset = (<any>field.offsetParent).offsetParent.offsetParent.offsetTop;
						}
						if (field.className.indexOf('nha-sa-residency-container') > -1 || field.className.indexOf('nha-invalid-birthdate') > -1) {
							offset = field.offsetTop;
						}
						window.scrollTo(0, offset - 25);
						break;
					}
				}
				this.isSaving = false;
			} else {
				this.appData = new Application();
				const enrollmentPreferenceIdValue = (this.guardianComponent.guardians.controls[0] as UntypedFormGroup).get('enrollmentPreferenceId').value;

				this.childComponent.mapApplicationForSchools(enrollmentPreferenceIdValue);
				this.childComponent.mapBirthDates();
				this.childComponent.mapLanguages();

				// mapping the data above takes a little bit of time so we need to delay briefly
				setTimeout(() => {
					this.appData.academicYearId = this.store.selectSnapshot(AcademicYearState.selectedYear);
					this.appData.guardians = this.store.selectSnapshot(GuardianState.guardians);
					this.appData.children = this.store.selectSnapshot(ChildState.children);
					this.appData.completedByInternalGuardianId = this.store.selectSnapshot(TermsConditionsState.completedByGuardian);
					this.appData.internalApplicationId = this.generateGuid();
					// LanguageID 1 = English, LanguageID 3 = Spanish
					const culture = this.store.selectSnapshot(RootState.culture);
					this.appData.enteredInLanguageId = culture.toLowerCase() === 'en-us' ? 1 : 3;
					this.appData.submissionDate = new Date().toISOString();
					this.uniqueHouseholds = [];
					this.appData.guardians.forEach((guardian) => {
						if (this.uniqueHouseholds.filter((h) => h.internalHouseholdId === guardian.household.internalHouseholdId).length < 1) {
							this.uniqueHouseholds.push(guardian.household);
						}
					});
					const nearbySchools = this.store.selectSnapshot(NearbySchoolsState.nearbySchoolsApplying);

					this.appData.children = this.appData.children.map((child) => {
						// default to Full Time if unset (NY schools)
						if (child.householdResidencies[0]?.residencyTypeId === '') {
							child.householdResidencies[0].residencyTypeId = '1';
						}

						if (nearbySchools && nearbySchools.length > 0) {
							child.applicationForSchools.splice(
								1,
								0,
								...nearbySchools.map(
									(ns) =>
										({
											schoolId: ns.schoolID,
											gradeLevelId: child.gradeLevelId
										} as ApplicationForSchool)
								)
							);
						}
						return child;
					});

					if (document.getElementById('nha-sa-ma-tracker') != null) {
						const maEmail = (document.getElementById('nha-sa-ma-email') as any).value;
						const crmLeadId = (document.getElementById('nha-sa-crm-lead-id') as any).value;

						if (maEmail !== '' && crmLeadId !== '') {
							this.appData.crmLeadId = crmLeadId;
							this.appData.completedByEmail = maEmail;
						}
					}
                    else {
						this.appData.crmLeadId = this.store.selectSnapshot(RootState.content).crmGuid;
					}

					this.saveService.saveApplication(this.appData).subscribe((data) => {
						this.fireGoogleEvent(this.appData.internalApplicationId);
						this.isSaving = false;
						this.isSubmitted = true;
					});
				}, 500);
			}
		}, 100);
	}

	@HostListener('window:afterprint', ['$event'])
	revertTerms(): void {
		this.showTermsAndConditions = false;
	}

	@HostListener('window:beforeprint', ['$event'])
	async printListener(_) {
		this.showTermsAndConditions = true;

		await new Promise((r) => setTimeout(r, 250));

		if (this.readCookie('googtrans')) {
			document.getElementById('print-link-scrollto').scrollIntoView({ behavior: 'smooth' });

			await new Promise((r) => setTimeout(r, 1000));
		}
	}

	async printApplication() {
		this.showTermsAndConditions = true;

		await new Promise((r) => setTimeout(r, 250));

		if (this.readCookie('googtrans')) {
			document.getElementById('print-link-scrollto').scrollIntoView({ behavior: 'smooth' });

			await new Promise((r) => setTimeout(r, 1000));
		}

		window.print();
	}

	readCookie(name) {
		var c = document.cookie.split('; '),
			cookies = {},
			i,
			C;

		for (i = c.length - 1; i >= 0; i--) {
			C = c[i].split('=');
			cookies[C[0]] = C[1];
		}

		return cookies[name];
	}

	private generateGuid(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			// tslint:disable-next-line: no-bitwise
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	private fireGoogleEvent(internalApplicationId: string): void {
		if (window['_gaq']) {
			window['_gaq'].push(['_setAccount', window['_nha_gag']]);
			window['_gaq'].push(['_trackEvent', 'StudentApplication', 'Submitted', internalApplicationId]);
		}
	}
}
